import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Raven from 'raven-js';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { createManyEvents, deleteEvent, fetchEventList // @ts-expect-error importing from JS file
} from 'AdsUI/actions/EventSettingsActions'; // @ts-expect-error importing from JS file

import { postTimedNotification } from 'AdsUI/actions/NotificationActions'; // @ts-expect-error importing from JS file

import { saveUserAttribute } from 'AdsUI/actions/UserAttributesActions'; // @ts-expect-error importing from JS file

import { SEEN_FACEBOOK_CAPI_LCS_MODAL } from 'AdsUI/data/UserAttributesData'; // @ts-expect-error importing from JS file

import { usageTracker } from 'AdsUI/lib/usageTracker';
import { OfflineEventTriggers, EVENT_CREATION_ERROR_TYPES, EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS // @ts-expect-error importing from JS file
} from 'AdsUI/lib/Constants';
import { Metrics } from 'AdsUI/Metrics'; // @ts-expect-error importing from JS file

import { getHubSpotUserId } from 'AdsUI/selectors/AuthSelectors';

/**
 * Determine if the form has errors.
 * @param eventOptions The event options chosen by the user
 * @param accountsWithoutBusinessManager Ad accounts without a business manager
 * @returns True or false
 */
const formHasErrors = (eventOptions, accountsWithoutBusinessManager) => {
  const {
    currentEventTrigger,
    selectedAdAccount
  } = eventOptions;

  if (!selectedAdAccount) {
    return true;
  }

  const {
    adAccount
  } = selectedAdAccount;

  if (!currentEventTrigger || !adAccount || !eventOptions.selectedPixel) {
    return true;
  }

  const accountWithoutBusinessManager = accountsWithoutBusinessManager.has(adAccount);
  const numSelectedLifecycleStages = eventOptions.facebookCapiLSC && eventOptions.facebookCapiLSC.numSelectedLifecycleStages;
  return accountWithoutBusinessManager || !numSelectedLifecycleStages;
};
/**
 * Get the new events to create.
 * @param eventOptions The event options chosen by the user
 * @returns The network, ad account ID, and events to send to the necessary API endpoint
 */


const getEventsToCreate = eventOptions => {
  const {
    network,
    accountId
  } = eventOptions.selectedAdAccount ? eventOptions.selectedAdAccount.adAccount : {
    network: null,
    accountId: null
  };
  const baseEventOptions = {
    eventTriggerType: OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE,
    pixelId: eventOptions.selectedPixel && eventOptions.selectedPixel.id,
    allowEventStreamingForContactsWithoutAdInteraction: eventOptions.allowEventStreamingForContactsWithoutAdInteraction
  };
  const eventsToCreate = eventOptions.facebookCapiLSC && eventOptions.facebookCapiLSC.selectedLifecycleStages ? eventOptions.facebookCapiLSC.selectedLifecycleStages.map(lifecycleStageId => Object.assign({}, baseEventOptions, {
    eventName: `HubSpot - ${I18n.text(`events.creation.CRM_EVENT.lifecycleStages.networkEvents.FACEBOOK_CAPI.${lifecycleStageId}`)}`,
    lifecycleStageId
  })) : [];
  return {
    network,
    accountId,
    eventsToCreate
  };
};
/**
 * Record metrics for the submission of the given events.
 * @param network The selected ad network
 * @param createdEvents The events that were created
 */


const recordSubmissionMetrics = (network, createdEvents) => {
  // Track each new lifecycle stage
  createdEvents.forEach(event => {
    usageTracker.track('modifyEvent', {
      action: 'Created event',
      network,
      trigger: OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE,
      lifecycleStage: event.lifecycleStageId
    });
  }); // Track number of submitted lifecycle stages

  usageTracker.track('modifyEvent', {
    action: 'Created event',
    network,
    trigger: OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE,
    submittedLifecycleStagesCount: createdEvents.length
  });
  Metrics.counter('created-event', {
    network
  }).increment(createdEvents.length);
};
/**
 * Button for creating Facebook CAPI events.
 */


const FacebookCapiEventCreationButton = ({
  locked,
  eventOptions,
  toggleIsOpen,
  setShowErrors,
  accountsWithoutBusinessManager,
  setHasIncorrectBusinessManagerPermissions,
  eventToReplace
}) => {
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const userId = useSelector(getHubSpotUserId);
  const dispatch = useDispatch();
  /** Delete the event that the user has selected to replace. */

  const deleteEventToReplace = () => {
    if (eventToReplace) {
      return dispatch(deleteEvent(eventToReplace.id, true)).catch(error => {
        Raven.captureException(error);
      });
    }

    return null;
  };
  /**
   * Redirect the user to the events page if they are replacing an event that
   * was opened in the detail panel. Redirecting is necessary because the event
   * whose detail panel was open has been deleted.
   */


  const redirectToEventsPage = () => {
    // Matches "/events/123456" where "123456" can be of any length >= 1
    if (/\/events\/[0-9]+/.test(history.location.pathname)) {
      history.push('/events');
    }
  };

  const handleSubmit = () => {
    if (formHasErrors(eventOptions, accountsWithoutBusinessManager)) {
      setShowErrors(true);
      return;
    }

    const {
      network,
      accountId,
      eventsToCreate
    } = getEventsToCreate(eventOptions);

    if (!network || !accountId || !eventsToCreate || !eventsToCreate.length) {
      setShowErrors(true);
      return;
    }

    setLoading(true);
    dispatch(createManyEvents(network, accountId, eventsToCreate)).then(() => {
      return deleteEventToReplace();
    }).then(() => {
      dispatch(postTimedNotification({
        id: 'create-fb-capi-event-success',
        type: 'success',
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `events.creation.banners.${eventToReplace ? 'replaceEventSuccess' : 'manySuccesses'}`
        })
      }, 4000));
      dispatch(fetchEventList()); // Prevent the user from seeing the Facebook CAPI modal

      dispatch(saveUserAttribute(SEEN_FACEBOOK_CAPI_LCS_MODAL, userId, true));
      recordSubmissionMetrics(network, eventsToCreate);
      toggleIsOpen();
      redirectToEventsPage();
    }).catch(error => {
      setFailed(true);
      const errorType = error.cause && error.cause.errorType || '';
      const errorMessage = error.cause && error.cause.message || '';

      if (errorType === EVENT_CREATION_ERROR_TYPES.APP_SCOPE && errorMessage.includes(EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS.MODIFY_OFFLINE_CONVERSION_DATA_SETS)) {
        setShowErrors(true);
        setHasIncorrectBusinessManagerPermissions(true);
      }
    }).finally(() => {
      setLoading(false);
    });
  };

  return /*#__PURE__*/_jsx(UILoadingButton, {
    use: "primary",
    disabled: locked,
    onClick: handleSubmit,
    loading: loading,
    failed: failed,
    "data-test-id": "fb-capi-create-event-button",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "events.creation.createManyEventsButton",
      options: {
        count: eventOptions.facebookCapiLSC && eventOptions.facebookCapiLSC.numSelectedLifecycleStages
      }
    })
  });
};

export default FacebookCapiEventCreationButton;